/**
 * Curve fitting utilities for histogram distributions
 */

export type CurveType = "none" | "polynomial" | "power-law" | "exponential";

export type FitResult = {
  type: CurveType;
  formula: string;
  coefficients: number[];
  rSquared: number;
  predict: (x: number) => number;
};

type Point = { x: number; y: number };

type ActualVsPredicted = { actual: number; predicted: number };

/**
 * Element `i` of a dense array, where the surrounding loop or the array's
 * construction keeps `i` in bounds. Throws rather than defaulting, because a
 * substituted zero would silently distort a coefficient or a residual.
 */
function at<T>(values: T[], i: number): T {
  const value = values[i];
  if (value === undefined) {
    throw new Error(`Index ${String(i)} is outside the array`);
  }
  return value;
}

/**
 * Calculate mean of an array
 */
function mean(arr: number[]): number {
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

/**
 * Calculate R² (coefficient of determination)
 * R² = 1 - (SS_res / SS_tot)
 */
function calculateRSquared(observations: ActualVsPredicted[]): number {
  if (observations.length === 0) return 0;

  // Non-finite predictions can't yield a meaningful R²; 0 is the safe fallback
  // (no console noise — this runs in a shared fitting utility).
  if (observations.some((r) => !isFinite(r.predicted))) {
    return 0;
  }

  const yMean = mean(observations.map((r) => r.actual));
  const ssTot = observations.reduce(
    (sum, { actual }) => sum + Math.pow(actual - yMean, 2),
    0,
  );
  const ssRes = observations.reduce(
    (sum, { actual, predicted }) => sum + Math.pow(actual - predicted, 2),
    0,
  );
  if (ssTot === 0) return 1; // Perfect fit when all values are identical
  if (!isFinite(ssRes)) return 0; // Handle overflow
  return Math.max(0, 1 - ssRes / ssTot); // Clamp to 0-1
}

/**
 * Polynomial regression using least squares
 * Fits y = a₀ + a₁x + a₂x² + ... + aₙxⁿ
 */
function polynomialRegression(points: Point[], degree: number): number[] {
  if (points.length === 0) return new Array<number>(degree + 1).fill(0);

  // Solve the normal equations X'X * coeffs = X'y, as one augmented matrix:
  // row i is the i-th row of X'X followed by the i-th entry of X'y.
  const augmented = Array.from({ length: degree + 1 }, (_, i) => [
    ...Array.from({ length: degree + 1 }, (_, j) =>
      points.reduce((sum, p) => sum + Math.pow(p.x, i + j), 0),
    ),
    points.reduce((sum, p) => sum + Math.pow(p.x, i) * p.y, 0),
  ]);

  return solveLinearSystem(augmented);
}

/**
 * Solve the augmented system [A | b] using Gaussian elimination with partial
 * pivoting. `augmented` has n rows of n + 1 entries.
 */
function solveLinearSystem(augmented: number[][]): number[] {
  const n = augmented.length;

  // Forward elimination with partial pivoting
  for (let col = 0; col < n; col++) {
    // Find pivot
    let maxRow = col;
    for (let row = col + 1; row < n; row++) {
      if (
        Math.abs(at(at(augmented, row), col)) >
        Math.abs(at(at(augmented, maxRow), col))
      ) {
        maxRow = row;
      }
    }
    // Swap rows
    const pivotRow = at(augmented, col);
    augmented[col] = at(augmented, maxRow);
    augmented[maxRow] = pivotRow;

    // Check for zero pivot
    const pivot = at(at(augmented, col), col);
    if (Math.abs(pivot) < 1e-10) continue;

    // Eliminate
    const eliminationRow = at(augmented, col);
    for (let row = col + 1; row < n; row++) {
      const target = at(augmented, row);
      const factor = at(target, col) / pivot;
      for (let j = col; j <= n; j++) {
        target[j] = at(target, j) - factor * at(eliminationRow, j);
      }
    }
  }

  // Back substitution
  const x = new Array<number>(n).fill(0);
  for (let row = n - 1; row >= 0; row--) {
    const rowValues = at(augmented, row);
    const pivot = at(rowValues, row);
    if (Math.abs(pivot) < 1e-10) continue;
    let sum = at(rowValues, n);
    for (let col = row + 1; col < n; col++) {
      sum -= at(rowValues, col) * at(x, col);
    }
    x[row] = sum / pivot;
  }

  return x;
}

/**
 * Format a number with appropriate precision
 */
function formatCoeff(val: number): string {
  if (Math.abs(val) < 0.001) return val.toExponential(2);
  if (Math.abs(val) >= 1000) return val.toExponential(2);
  return val.toFixed(3).replace(/\.?0+$/, "");
}

/**
 * Fit a polynomial curve to histogram bin data
 */
export function fitPolynomial(
  bins: { min: number; max: number; count: number }[],
  degree: number = 2,
): FitResult {
  // Use bin midpoints as x values and counts as y values
  const points = bins.map((b) => ({ x: (b.min + b.max) / 2, y: b.count }));

  const coeffs = polynomialRegression(points, degree);

  const predict = (xi: number) =>
    coeffs.reduce((sum, c, i) => sum + c * Math.pow(xi, i), 0);

  const rSquared = calculateRSquared(
    points.map((p) => ({ actual: p.y, predicted: predict(p.x) })),
  );

  // Build formula string
  let formula = "";
  for (let i = degree; i >= 0; i--) {
    const c = at(coeffs, i);
    if (Math.abs(c) < 1e-10 && i > 0) continue;

    const sign =
      c >= 0 && formula.length > 0
        ? " + "
        : formula.length > 0
          ? " - "
          : c < 0
            ? "-"
            : "";
    const absC = formatCoeff(Math.abs(c));

    if (i === 0) {
      formula += `${sign}${absC}`;
    } else if (i === 1) {
      formula += `${sign}${absC}x`;
    } else {
      formula += `${sign}${absC}x²`.replace("²", `^${String(i)}`);
    }
  }

  // Simplify x^2 display
  formula = formula.replace(/\^2/g, "²").replace(/\^3/g, "³");

  return {
    type: "polynomial",
    formula: `y = ${formula || "0"}`,
    coefficients: coeffs,
    rSquared,
    predict,
  };
}

/**
 * Fit power law: y = a * x^b
 * Using log transform: ln(y) = ln(a) + b*ln(x)
 * Note: Power law requires x > 0 and works best when x >= 1
 */
export function fitPowerLaw(
  bins: { min: number; max: number; count: number }[],
): FitResult {
  // Filter out zero counts and require midpoint >= 1 to avoid numerical explosion
  // Power law with negative exponent blows up as x approaches 0
  const validBins = bins.filter((b) => {
    const midpoint = (b.min + b.max) / 2;
    return b.count > 0 && midpoint >= 1;
  });

  if (validBins.length < 2) {
    return {
      type: "power-law",
      formula: "y = (insufficient data with x ≥ 1)",
      coefficients: [0, 0],
      rSquared: 0,
      predict: () => 0,
    };
  }

  // Log transform
  const logPoints = validBins.map((b) => ({
    x: Math.log((b.min + b.max) / 2),
    y: Math.log(b.count),
  }));

  // Check for invalid log values
  if (logPoints.some((p) => !isFinite(p.x) || !isFinite(p.y))) {
    return {
      type: "power-law",
      formula: "y = (invalid data for log transform)",
      coefficients: [0, 0],
      rSquared: 0,
      predict: () => 0,
    };
  }

  // Linear regression on log values: logY = logA + b*logX
  const coeffs = polynomialRegression(logPoints, 1);
  const logA = at(coeffs, 0);
  const b = at(coeffs, 1);

  // Check for NaN coefficients
  if (!isFinite(logA) || !isFinite(b)) {
    return {
      type: "power-law",
      formula: "y = (regression failed)",
      coefficients: [0, 0],
      rSquared: 0,
      predict: () => 0,
    };
  }

  const a = Math.exp(logA);

  const predict = (xi: number) => (xi > 0 ? a * Math.pow(xi, b) : 0);

  // Calculate R² in log space (where the fit was actually performed)
  // This is the standard approach for power law fits
  const rSquared = calculateRSquared(
    logPoints.map((p) => ({ actual: p.y, predicted: logA + b * p.x })),
  );

  return {
    type: "power-law",
    formula: `y = ${formatCoeff(a)} × x^${formatCoeff(b)}`,
    coefficients: [a, b],
    rSquared,
    predict,
  };
}

/**
 * Fit exponential decay: y = a * e^(-bx)
 * Using log transform: ln(y) = ln(a) - b*x
 */
export function fitExponential(
  bins: { min: number; max: number; count: number }[],
): FitResult {
  // Filter out zero counts for log transform
  const validBins = bins.filter((b) => b.count > 0);

  if (validBins.length < 2) {
    return {
      type: "exponential",
      formula: "y = (insufficient data)",
      coefficients: [0, 0],
      rSquared: 0,
      predict: () => 0,
    };
  }

  // Log transform of the counts only; x stays linear
  const points = validBins.map((b) => ({
    x: (b.min + b.max) / 2,
    y: Math.log(b.count),
  }));

  // Linear regression: logY = logA - b*x → logY = logA + (-b)*x
  const coeffs = polynomialRegression(points, 1);
  const logA = at(coeffs, 0);
  const negB = at(coeffs, 1);
  const a = Math.exp(logA);
  const b = -negB;

  const predict = (xi: number) => a * Math.exp(-b * xi);

  // Calculate R² in log space (where the fit was actually performed)
  const rSquared = calculateRSquared(
    points.map((p) => ({ actual: p.y, predicted: logA - b * p.x })),
  );

  const decaySign = b >= 0 ? "-" : "+";
  const absB = Math.abs(b);

  return {
    type: "exponential",
    formula: `y = ${formatCoeff(a)} × e^(${decaySign}${formatCoeff(absB)}x)`,
    coefficients: [a, b],
    rSquared,
    predict,
  };
}

/**
 * Fit all curve types and return results
 */
export function fitAllCurves(
  bins: { min: number; max: number; count: number }[],
): { polynomial: FitResult; powerLaw: FitResult; exponential: FitResult } {
  return {
    polynomial: fitPolynomial(bins, 2),
    powerLaw: fitPowerLaw(bins),
    exponential: fitExponential(bins),
  };
}

/**
 * Find the best fitting curve with R² above threshold
 */
export function findBestFit(
  bins: { min: number; max: number; count: number }[],
  minRSquared: number = 0.5,
): { type: CurveType; fit: FitResult } | null {
  const fits = fitAllCurves(bins);

  const candidates: { type: CurveType; fit: FitResult }[] = [
    { type: "polynomial", fit: fits.polynomial },
    { type: "power-law", fit: fits.powerLaw },
    { type: "exponential", fit: fits.exponential },
  ];

  // Filter by minimum R² and sort by R² descending
  const validFits = candidates
    .filter((c) => c.fit.rSquared >= minRSquared)
    .sort((a, b) => b.fit.rSquared - a.fit.rSquared);

  return validFits[0] ?? null;
}

/**
 * Generate points for drawing a fitted curve
 */
export function generateCurvePoints(
  fit: FitResult,
  minX: number,
  maxX: number,
  numPoints: number = 100,
): { x: number; y: number }[] {
  const points: { x: number; y: number }[] = [];
  const step = (maxX - minX) / (numPoints - 1);

  for (let i = 0; i < numPoints; i++) {
    const x = minX + i * step;
    const y = Math.max(0, fit.predict(x)); // Clamp to non-negative
    points.push({ x, y });
  }

  return points;
}
